// src/components/VehicleDetails.jsx
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Paper,
  Typography,
  Snackbar,
  Alert,
  FormHelperText,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import VehicleServices from '../../services/externalServices/trackingservices/vehicleServices';
import OwnerServices from '../../services/externalServices/trackingservices/ownerServices';

const VEHICLE_TYPES = ['Bus', 'Van', 'Car'];
const APPROVALS = ['Pending', 'Approved', 'Rejected'];
const TRACKING_METHODS = ['NONE', 'MOBILE_APP', 'GPS_DEVICE'];

export default function VehicleDetails() {
  const { id } = useParams();
  const isEdit = Boolean(id);

  /* ================= ROLE CHECK ================= */
  const adminData = localStorage.getItem('admin');
  const admin = adminData ? JSON.parse(adminData) : null;
  const isSuperAdmin = admin?.role === 'superAdmin';
  const isReadOnly = !isSuperAdmin;

  /* ================= STATE ================= */
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [ownerList, setOwnerList] = useState([]);

  const [form, setForm] = useState({
    vehicle_number: '',
    alias_number: '',
    chassisNumber: '',
    vehicle_type: '',
    capacity: '',
    assigned_owner_id: '',
    approval_status: 'Pending',
    trackingMethod: 'NONE',
    defaultPartner: {
      name: '',
      phoneNumber: '',
    },
    created_at: null,
  });

  /* ================= SNACKBAR ================= */
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  /* ================= FETCH VEHICLE ================= */
  useEffect(() => {
    if (!isEdit) return;

    (async () => {
      try {
        const data = await VehicleServices.getVehicleById(id);
        setForm({
          ...data,
          capacity: data.capacity ?? '',
          defaultPartner: data.defaultPartner || { name: '', phoneNumber: '' },
        });
      } catch (e) {
        showSnackbar('Failed to load vehicle details', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  /* ================= FETCH OWNERS ================= */
  useEffect(() => {
    (async () => {
      try {
        const res = await OwnerServices.getAllOwners();
        setOwnerList(res || []);
      } catch (e) {
        showSnackbar('Failed to load owners', 'error');
      }
    })();
  }, []);

  /* ================= HANDLERS ================= */
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePartnerChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      defaultPartner: {
        ...prev.defaultPartner,
        [field]: value,
      },
    }));
  };

  const validate = () => {
    const errs = {};
    if (!form.vehicle_number) errs.vehicle_number = 'Vehicle number is required';
    if (!form.vehicle_type) errs.vehicle_type = 'Vehicle type is required';
    if (!form.capacity) errs.capacity = 'Capacity is required';
    if (!form.assigned_owner_id) errs.assigned_owner_id = 'Owner is required';
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!isSuperAdmin) {
      showSnackbar('You do not have permission to edit', 'warning');
      return;
    }

    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      setSubmitting(true);

      const payload = {
        ...form,
        capacity: Number(form.capacity),
      };

      if (isEdit) {
        await VehicleServices.updateVehicleById(id, payload);
      } else {
        await VehicleServices.createVehicle(payload);
      }

      showSnackbar(isEdit ? 'Vehicle updated successfully' : 'Vehicle created successfully');
    } catch (e) {
      showSnackbar('Save failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /* ================= UI ================= */
  return (
    <Paper sx={{ p: 2, maxWidth: 1000, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          {isEdit ? 'Vehicle Details' : 'Add Vehicle'}
        </Typography>
      </Stack>

      {loading ? (
        <Box p={2}>Loading...</Box>
      ) : (
        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            {/* Vehicle Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Vehicle Number"
                value={form.vehicle_number}
                onChange={(e) => handleChange('vehicle_number', e.target.value)}
                error={!!errors.vehicle_number}
                helperText={errors.vehicle_number}
                fullWidth
                disabled={isReadOnly}
              />
            </Grid>

            {/* Alias */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Alias Number"
                value={form.alias_number}
                onChange={(e) => handleChange('alias_number', e.target.value)}
                fullWidth
                disabled={isReadOnly}
              />
            </Grid>

            {/* Chassis */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Chassis Number"
                value={form.chassisNumber}
                onChange={(e) => handleChange('chassisNumber', e.target.value)}
                fullWidth
                disabled={isReadOnly}
              />
            </Grid>

            {/* Vehicle Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.vehicle_type}>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  value={form.vehicle_type}
                  label="Vehicle Type"
                  onChange={(e) => handleChange('vehicle_type', e.target.value)}
                  disabled={isReadOnly}
                >
                  {VEHICLE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Capacity */}
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                label="Capacity"
                value={form.capacity}
                onChange={(e) => handleChange('capacity', e.target.value)}
                error={!!errors.capacity}
                helperText={errors.capacity}
                fullWidth
                disabled={isReadOnly}
              />
            </Grid>

            {/* Owner */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.assigned_owner_id}>
                <InputLabel>Assigned Owner</InputLabel>
                <Select
                  value={form.assigned_owner_id}
                  label="Assigned Owner"
                  onChange={(e) => handleChange('assigned_owner_id', e.target.value)}
                  disabled={isReadOnly}
                >
                  {ownerList.map((o) => (
                    <MenuItem key={o._id} value={o._id}>
                      {o.name || o.email}
                    </MenuItem>
                  ))}
                </Select>
                {errors.assigned_owner_id && (
                  <FormHelperText>{errors.assigned_owner_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Approval */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Approval Status</InputLabel>
                <Select
                  value={form.approval_status}
                  label="Approval Status"
                  onChange={(e) => handleChange('approval_status', e.target.value)}
                  disabled={isReadOnly}
                >
                  {APPROVALS.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Tracking */}
            <Grid item xs={12}>
              <FormLabel>Tracking Method</FormLabel>
              <RadioGroup row value={form.trackingMethod}>
                {TRACKING_METHODS.map((m) => (
                  <FormControlLabel
                    key={m}
                    value={m}
                    control={<Radio disabled={isReadOnly} />}
                    label={m}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {/* Partner Details */}
            <Grid item xs={12}>
              <FormLabel>Default Partner (Optional)</FormLabel>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Partner Name"
                value={form.defaultPartner?.name || ''}
                onChange={(e) => handlePartnerChange('name', e.target.value)}
                fullWidth
                disabled={isReadOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Partner Phone"
                value={form.defaultPartner?.phoneNumber || ''}
                onChange={(e) => handlePartnerChange('phoneNumber', e.target.value)}
                fullWidth
                disabled={isReadOnly}
              />
            </Grid>
          </Grid>

          {/* ACTION */}
          <Stack direction="row" mt={3}>
            <Button
              type="submit"
              variant="contained"
              disabled={isReadOnly || submitting}
            >
              {isReadOnly ? 'View Only' : isEdit ? 'Save Changes' : 'Create Vehicle'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
